import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { pbdqFont } from '../theme/pbdqTokens';
import pbdqRoutes from '../pbdqRoutes';

const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(56px, 7vw, 96px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    stagesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 12,
        '@media (max-width: 960px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        '@media (max-width: 560px)': {
            gridTemplateColumns: '1fr',
        },
    },
    stageTab: {
        position: 'relative',
        display: 'grid',
        gap: 12,
        alignContent: 'start',
        textAlign: 'left',
        padding: '18px 18px 20px',
        borderRadius: 18,
        color: '#1B2430',
        cursor: 'pointer',
        fontFamily: pbdqFont,
        transition: 'background 240ms cubic-bezier(.32,.72,0,1), border-color 240ms cubic-bezier(.32,.72,0,1), transform 240ms cubic-bezier(.32,.72,0,1)',
        boxSizing: 'border-box',
        outline: 'none',
        '&:hover': {
            transform: 'translateY(-2px)',
        },
    },
    tabTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
    },
    tabBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: 12,
        flexShrink: 0,
    },
    tabNum: {
        fontSize: 12,
        fontWeight: 700,
        color: '#6B7280',
        fontVariantNumeric: 'tabular-nums',
    },
    tabVerb: {
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        fontWeight: 700,
        fontFamily: pbdqFont,
    },
    tabName: {
        fontSize: 16,
        fontWeight: 800,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
        fontFamily: pbdqFont,
        margin: 0,
    },
    tabArrow: {
        position: 'absolute',
        right: -12,
        top: '50%',
        width: 12,
        textAlign: 'center',
        transform: 'translateY(-50%)',
        color: '#B8C0CC',
        fontSize: 18,
        lineHeight: 1,
        zIndex: 1,
        pointerEvents: 'none',
        '@media (max-width: 960px)': {
            display: 'none',
        },
    },

    panel: {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: 16,
        borderRadius: 24,
        overflow: 'hidden',
        border: '1px solid #E6E9EE',
        boxShadow: '0 20px 50px rgba(23, 41, 57, 0.08)',
        background: '#FFFFFF',
    },
    panelLeft: {
        flex: '1 1 320px',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(28px, 3.4vw, 44px)',
        background: '#172939',
        color: '#FFFFFF',
        display: 'grid',
        gap: 18,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    panelWatermark: {
        position: 'absolute',
        right: -6,
        bottom: -30,
        fontSize: 'clamp(140px, 16vw, 220px)',
        fontWeight: 800,
        lineHeight: 1,
        color: 'rgba(255, 255, 255, 0.06)',
        letterSpacing: '-0.05em',
        pointerEvents: 'none',
        fontVariantNumeric: 'tabular-nums',
        userSelect: 'none',
    },
    panelIconWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 84,
        height: 84,
        borderRadius: 18,
        position: 'relative',
        flexShrink: 0,
    },
    panelStagePill: {
        display: 'inline-flex',
        width: 'max-content',
        alignItems: 'center',
        padding: '7px 12px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        position: 'relative',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    panelTitle: {
        fontSize: 'clamp(28px, 3vw, 40px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        position: 'relative',
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    panelPromise: {
        fontSize: 'clamp(17px, 1.4vw, 20px)',
        lineHeight: 1.45,
        fontWeight: 600,
        margin: 0,
        color: '#F5BE9F',
        position: 'relative',
        maxWidth: '36ch',
        fontFamily: pbdqFont,
    },

    panelRight: {
        flex: '1.4 1 400px',
        minWidth: 0,
        padding: 'clamp(28px, 3.4vw, 44px)',
        background: '#FFFFFF',
        display: 'grid',
        gap: 24,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    panelBody: {
        fontSize: 16,
        lineHeight: 1.65,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    metaGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
        gap: 24,
    },
    metaCol: {
        borderTop: '1px solid #E6E9EE',
        paddingTop: 14,
        display: 'grid',
        gap: 8,
        alignContent: 'start',
    },
    metaLabel: {
        fontSize: 11.5,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
        fontFamily: pbdqFont,
    },
    metaRoleText: {
        fontSize: 14.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    usedInWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
    },
    usedInChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '6px 11px',
        borderRadius: 999,
        background: '#F4F6F9',
        fontSize: 12.5,
        fontWeight: 600,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    usedInDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
    },
    usedInSubtext: {
        display: 'block',
        fontSize: 11,
        fontWeight: 500,
        color: '#6B7280',
        lineHeight: 1.25,
        marginTop: 2,
    },

    practiceBox: {
        padding: '20px 22px',
        borderRadius: 14,
        background: '#FBEDE4',
        borderLeft: '4px solid #ED6628',
        display: 'grid',
        gap: 8,
    },
    practiceLabel: {
        fontSize: 11.5,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#A63C0C',
        fontFamily: pbdqFont,
    },
    practiceProof: {
        fontSize: 15.5,
        lineHeight: 1.5,
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    practiceLink: {
        fontSize: 14,
        fontWeight: 700,
        color: '#A63C0C',
        textDecoration: 'none',
        fontFamily: pbdqFont,
        display: 'inline-block',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

const STAGES_DATA = [
    {
        verb: 'Define',
        name: 'FrameworkCreator',
        promise: 'Define a shared framework for AI-digital capacity, mapped to IEEE 3527.1 and 100+ global standards.',
        body: 'Agree what AI-digital literacy means for your people — aligned to recognized global standards — so everyone is working toward one clear target. FrameworkCreator combines productized tooling with expert-guided methodology, which is what keeps the result rigorous, consistent, and globally interoperable.',
        role: 'Defines a shared vision on AI-digital competencies and systems.',
        used: [
            { name: 'Workforce AI Readiness', color: '#ED6628' },
            { name: 'LearnCreator', subtext: '(optional national framework alignment)', color: '#EDB459' },
        ],
        proof: 'Gyeongsangbuk-do Province used FrameworkCreator to build a first-of-its-kind AI-digital competency framework and tiered certification system.',
        bar: '#68C3EE',
        tint: '#E1F0F9',
        ink: '#1F5F86',
        icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    },
    {
        verb: 'Measure',
        name: 'AssessCreator',
        promise: 'Measure AI-digital capacity with standardized or custom assessments — across individuals, cohorts, and whole populations, tracked in real time.',
        body: 'Design, deploy, and analyze assessments with multi-level dashboards and real-time analytics at individual, group, organization, and sector level.',
        role: 'Measures a baseline and establishes an evidence layer for action',
        used: [
            { name: 'COS Assessments', color: '#68C3EE' },
            { name: 'School COS Readiness', color: '#BCD253' },
            { name: 'Workforce AI Readiness', color: '#ED6628' },
            { name: 'Program Validation', color: '#B9316D' },
        ],
        proof: 'National University of Singapore (NUS) used AssessCreator to measure digital wellbeing of 4.000+ people across four different countries.',
        bar: '#BCD253',
        tint: '#EDF2D5',
        ink: '#4E6A12',
        icon: 'M3 3v18h18 M7 15l4-5 4 3 5-7',
    },
    {
        verb: 'Build',
        name: 'LearnCreator',
        promise: 'Build custom programs that educate, train, and engage on AI-digital capacity — with impact measured throughout.',
        body: 'An AI-powered program creation engine that generates complete learning experiences with assessment embedded. It compresses program creation from months or years into days, at a fraction of conventional development cost — even for teams with no prior curriculum expertise.',
        role: 'Create learning programs with built-in assessments',
        used: [
            { name: 'School COS Readiness', color: '#BCD253' },
            { name: 'LearnCreator', color: '#EDB459' },
            { name: 'Workforce AI Readiness', color: '#ED6628' },
        ],
        proof: 'The LEGO Group built digital citizenship programs using Powered by DQ and reached millions of children.',
        bar: '#EDB459',
        tint: '#FAEDDA',
        ink: '#8A5A10',
        icon: 'm12 2 9 5-9 5-9-5 9-5z M3 12l9 5 9-5 M3 17l9 5 9-5',
    },
    {
        verb: 'Validate',
        name: 'DQ Stars',
        promise: 'Validate frameworks, assessments, and programs against global standards, and earn recognition.',
        body: 'DQ Stars measures how well an existing framework, assessment, or piece of learning content aligns to the IEEE DQ standard and other 100+ established frameworks — so funders, regulators, and partners can trust it, and so quality programs can differentiate themselves and scale.',
        role: 'Validates any program against global standards.',
        used: [
            { name: 'Available as an add-on to every solution', color: '#B8C0CC' },
        ],
        proof: 'Alannah & Madeline Foundation, Accenture, and Netsafe used DQ Stars to align their online safety program with global standards and frameworks.',
        bar: '#ED6628',
        tint: '#FBEDE4',
        ink: '#A63C0C',
        icon: 'm12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2z',
    },
    {
        verb: 'Benchmark',
        name: 'DQ Index Connect',
        promise: 'Benchmark AI-digital capacity against the world, and see your standing in national and global context.',
        body: 'Turn your measurement data into comparative intelligence and policy insight by connecting to the world’s largest digital intelligence databank. Most often deployed alongside AssessCreator — benchmarking only means something when there is underlying measurement behind it.',
        role: 'Benchmarks against the national and global digital intelligence dataset.',
        used: [
            { name: 'COS Assessments', color: '#68C3EE' },
            { name: 'Available as an add-on to every solution', color: '#B8C0CC' },
        ],
        proof: 'The Global Cybersecurity Forum (GCF) used DQ Index Connect to compare each country\'s child online safety data against global benchmarks.',
        bar: '#B9316D',
        tint: '#F6E5F0',
        ink: '#8E1F55',
        icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M2 12h20 M12 2a15 15 0 0 1 0 20 M12 2a15 15 0 0 0 0 20',
    },
];

const PbdqCapabilityTabs = () => {
    const classes = useStyles();
    const [sel, setSel] = useState(0);
    const d = STAGES_DATA[sel];

    return (
        <div className={classes.container}>
            <div className={classes.stagesGrid} role="tablist" aria-label="Capability">
                {STAGES_DATA.map((s, i) => {
                    const isSelected = i === sel;
                    return (
                        <button
                            key={s.name}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            onClick={() => setSel(i)}
                            className={classes.stageTab}
                            style={{
                                border: `1px solid ${isSelected ? s.bar : '#E6E9EE'}`,
                                borderTop: `4px solid ${s.bar}`,
                                background: isSelected ? s.tint : '#FFFFFF',
                            }}
                        >
                            <span className={classes.tabTopRow}>
                                <span
                                    className={classes.tabBadge}
                                    style={{ background: s.tint, color: s.ink }}
                                >
                                    <svg
                                        width="22"
                                        height="22"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d={s.icon} />
                                    </svg>
                                </span>
                                <span className={classes.tabNum}>0{i + 1}</span>
                            </span>
                            <span className={classes.tabVerb} style={{ color: s.ink }}>{s.verb}</span>
                            <h3 className={classes.tabName}>{s.name}</h3>
                            {i < 4 && (
                                <span className={classes.tabArrow} aria-hidden="true">›</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div
                role="tabpanel"
                className={classes.panel}
                style={{ borderTop: `4px solid ${d.bar}` }}
            >
                <div className={classes.panelLeft}>
                    <span className={classes.panelWatermark} aria-hidden="true">
                        0{sel + 1}
                    </span>
                    <span
                        className={classes.panelIconWrap}
                        style={{ background: d.tint, color: d.ink }}
                    >
                        <svg
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d={d.icon} />
                        </svg>
                    </span>
                    <span
                        className={classes.panelStagePill}
                        style={{ background: d.tint, color: d.ink }}
                    >
                        0{sel + 1} · {d.verb}
                    </span>
                    <h2 className={classes.panelTitle}>{d.name}</h2>
                    <p className={classes.panelPromise}>{d.promise}</p>
                </div>

                <div className={classes.panelRight}>
                    <p className={classes.panelBody}>{d.body}</p>
                    <div className={classes.metaGrid}>
                        <div className={classes.metaCol}>
                            <span className={classes.metaLabel}>Its role</span>
                            <p className={classes.metaRoleText}>{d.role}</p>
                        </div>
                        <div className={classes.metaCol}>
                            <span className={classes.metaLabel}>Used in</span>
                            <div className={classes.usedInWrap}>
                                {d.used.map((u) => (
                                    <span key={u.name} className={classes.usedInChip}>
                                        <span
                                            className={classes.usedInDot}
                                            style={{ background: u.color }}
                                        />
                                        <span>
                                            <span>{u.name}</span>
                                            {u.subtext && (
                                                <span className={classes.usedInSubtext}>{u.subtext}</span>
                                            )}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={classes.practiceBox}>
                        <span className={classes.practiceLabel}>In practice</span>
                        <p className={classes.practiceProof}>{d.proof}</p>
                        <Link to={pbdqRoutes.impact} className={classes.practiceLink}>
                            Read the case study in Impact →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PbdqCapabilityTabs;
